import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { take } from 'rxjs/operators';

import { ApiError, MessageService } from '../../../core';
import { MetaFilterSearch } from '../filter';
import { MetaCrudHttpService } from '../services';
import { IMultiSelectOption, IMultiSelectSettings, IMultiSelectTexts  } from 'ngx-bootstrap-multiselect';

@Component({
    selector: 'sgt-meta-multiselect',
    templateUrl: './meta-multiselect.component.html',
    styleUrls: ['./meta-multiselect.component.less']
})
export class MetaMultiselectComponent implements OnInit, OnChanges {
    /** Metas selecionadas */
    @Input() idsMetas: number[];
    /** Ao alterar a seleção das metas */
    @Output() idsMetasChange = new EventEmitter<number[]>();

    // Settings
    settings: IMultiSelectSettings;
    @Input() pullRight = false;
    @Input() enableSearch = true;
    @Input() showCheckAll = true;
    @Input() showUncheckAll = true;
    @Input() dynamicTitleMaxItems = 0;
    @Input() fixedTitle = false;
    @Input() selectionLimit = 0;
    @Input() autoUnselect = false;
    @Input() closeOnSelect = false;
    /** Se deve exibir o multiselect acima */
    @Input() dropup = false;
    /** Se deve desabilitar o multiselect */
    @Input() disabled = false;
    /** Se deve pesquisar as metas ao iniciar o multiselect */
    @Input() searchOnInit = true;

    // Texts
    texts: IMultiSelectTexts;
    @Input() checkAll = 'Selecionar Todas';
    @Input() uncheckAll = 'Limpar';
    @Input() defaultTitle = 'Nenhuma Meta';
    @Input() checked = 'Meta';
    @Input() checkedPlural = 'Metas';
    @Input() searchPlaceholder = 'Pesquisar...';
    @Input() allSelected = 'Todas Selecionadas';
    @Input() searchEmptyResult = 'Nenhuma meta encontrada';
    @Input() searchNoRenderText = 'Digite para pesquisar';

    // Options
    options: IMultiSelectOption[];

    private isFirstChange = true;

    constructor(private messageService: MessageService,
                private metaCrudHttpService: MetaCrudHttpService) { }

    ngOnInit() {
        // Settings
        this.createSettings();
        // Texts
        this.createTexts();

        if (this.searchOnInit) {
            this.create();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (propName === 'selectionLimit' && !changes[propName].isFirstChange()) {
                this.createSettings();
            } else if (propName === 'autoUnselect' && !changes[propName].isFirstChange()) {
                this.createSettings();
            } else if (propName === 'closeOnSelect' && !changes[propName].isFirstChange()) {
                this.createSettings();
            }
        }
    }

    /** Cria os settings do multiselect */
    private createSettings() {
        this.settings = {
            pullRight: this.pullRight,
            enableSearch: this.enableSearch,
            containerClasses: `container-multiselect container-multiselect-meta ${this.dropup ? 'dropup' : 'dropdown'}`,
            showCheckAll: this.showCheckAll,
            showUncheckAll: this.showUncheckAll,
            dynamicTitleMaxItems: this.dynamicTitleMaxItems,
            fixedTitle: this.fixedTitle,
            selectionLimit: this.selectionLimit,
            autoUnselect: this.autoUnselect,
            closeOnSelect: this.closeOnSelect
        };
    }

    /** Cria os texts do multiselect */
    private createTexts() {
        this.texts = {
            checkAll: this.checkAll,
            uncheckAll: this.uncheckAll,
            defaultTitle: this.defaultTitle,
            checked: this.checked,
            checkedPlural: this.checkedPlural,
            searchPlaceholder: this.searchPlaceholder,
            allSelected: this.allSelected,
            searchEmptyResult: this.searchEmptyResult,
            searchNoRenderText: this.searchNoRenderText
        };
    }

    /** Cria as metas para o multiselect */
    private create() {
        this.search()
            .then((() => {}))
            .catch(() => {});
    }

    /** Pesquisa as metas para o multiselect */
    search(filter: MetaFilterSearch = null): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.options = [];
            const selects: IMultiSelectOption[] = [];

            this.metaCrudHttpService.searchWithFilter(filter)
                .pipe(
                    take(1)
                )
                .subscribe(
                    entities => {
                        // Adicionando os tipos de itens ao multiselect
                        if (entities) {
                            entities.forEach(entity => {
                                selects.push({
                                    id: entity.id,
                                    name: entity.nome
                                });
                            });
                            this.options = selects;
                        }

                        resolve();
                    },
                    (error: ApiError) => {
                        this.messageService.error(error.message,  'Erro ao pesquisar as metas', error);
                        reject();
                    }
                );
        });
    }

    /** Ao alterar a seleção da meta */
    onChange() {
        if (this.isFirstChange) {
            this.isFirstChange = false;
            return;
        }

        this.idsMetasChange.emit(this.idsMetas);
    }

    hasOptions(): boolean {
        return this.options?.length > 0 ?? false;
    }

    /** Verifica se o multiselect possui a meta informada */
    possuiMeta(idMeta: number): boolean {
        if (!this.options) {
            return false;
        }

        const index = this.options.findIndex(option => option.id === idMeta);
        return index > -1;
    }
}
